// The placeholders the messaging editor can insert, and the rules for which of them are
// available in a given message.
//
// The labels here are a contract, not display text: hellohousing's backend substitutes real
// values by matching the exact label inside the placeholder anchor when a message is sent,
// so a renamed or misspelled label silently ships the literal word to the tenant. The same
// list is spelled out in the editor's placeholder popover (renderPlaceHoldersPopover in
// views/pages/Module/Messaging/MessagingEditor.jsx) and in the AI agent server's
// PLACEHOLDER_CATALOG (messageTemplateService.js) — all three must stay in sync.

// Maps the availability flags NewMessage.jsx builds (and passes down as the `placeholders`
// prop) onto the label each flag stands for. Flags with no entry here are ones the popover
// never renders, so they have no label to offer.
export const PLACEHOLDER_LABELS_BY_FLAG = {
  userName: 'Absender',
  billingPeriod: 'Abrechnungszeitraum',
  moveOutDate: 'Auszugsdatum',
  billingStatementTotalCosts: 'Betriebskosten',
  propertyOwner: 'Eigentümer',
  moveInDate: 'Einzugsdatum',
  recipient: 'Empfänger',
  recievedRent: 'erhaltene Miete',
  payableRent: 'gesamte ausstehende Miete',
  billingStatementBalance: 'Guthaben / Nachzahlung',
  netRent: 'Kaltmiete',
  tenant: 'Mieter',
  rentalUnit: 'Mieteinheit',
  rentMonth: 'Mietmonat',
  property: 'Objektadresse',
  companyName: 'Vermieter',
  billingStatementAdvancePayments: 'Vorauszahlungen',
  advancePayment: 'Vorauszahlung',
  totalRent: 'Warmmiete',
  accessLink: 'Zugangslink',
};

// Placeholders that resolve against a tenant record. A message addressed to an external
// recipient (one with no tenantId) has nothing to resolve them from, which is why the
// popover greys exactly these out once such a recipient is in the list — see the
// `externalTenantFound` argument in MessagingEditor's renderSinglePlaceHolder, and the
// disableAllTenantPlaceholders* pairs that mark them unusable in existing message HTML.
export const TENANT_PLACEHOLDER_LABELS = [
  'Auszugsdatum',
  'Einzugsdatum',
  'erhaltene Miete',
  'gesamte ausstehende Miete',
  'Kaltmiete',
  'Mieter',
  'Mieteinheit',
  'Mietmonat',
  'Vorauszahlung',
  'Warmmiete',
];

/**
 * The placeholder labels a user could insert into this message by hand, which is exactly the
 * set the AI is allowed to write into it. Anything the popover hides or greys out is
 * withheld from the model too: a generated message must not contain a placeholder the user
 * couldn't have added themselves, because at send time it would resolve to nothing.
 */
export const getAvailablePlaceholderLabels = (placeholders, { externalRecipientPresent = false } = {}) => {
  if (!placeholders) return [];
  return Object.entries(PLACEHOLDER_LABELS_BY_FLAG)
    .filter(([flag]) => Boolean(placeholders[flag]))
    .map(([, label]) => label)
    .filter((label) => !(externalRecipientPresent && TENANT_PLACEHOLDER_LABELS.includes(label)));
};

// The editor stores a placeholder as a bare anchor, so a generated message rendered outside
// the editor (e.g. the AI modal's preview) shows it as an unstyled link. Adding the class
// the Draft.js decorator would have applied gives it the same pill look. Display only —
// never feed the result back into the editor or into a save.
export const decoratePlaceholdersForPreview = (html) =>
  String(html || '').replace(/<a href="#"([^>]*)>/g, '<a href="#" class="rdw-mention-link"$1>');
